import React from "react";
import { KPIOverviewTileModel } from "../../types";
import {
  CHANGE_TONE_STYLES,
  STATUS_STYLES,
} from "../../utils/kpiOverviewStatus";
import { SPARKLINE_HEIGHT, SPARKLINE_WIDTH } from "../../utils/sparkline";

interface KPIOverviewTileProps {
  tile: KPIOverviewTileModel;
}

const NO_VALUE = "-";

/** Only an Off target value is colored; the dot and the line carry the other statuses. */
const OFF_TARGET_ONLY_VALUE_COLOR = "text-gray-900 dark:text-white";

const KPIOverviewTile: React.FC<KPIOverviewTileProps> = ({ tile }) => {
  const style = STATUS_STYLES[tile.status];
  const valueColor =
    tile.status === "off_target" ? style.value : OFF_TARGET_ONLY_VALUE_COLOR;
  const last = tile.sparkline.last;

  return (
    <div
      aria-label={`${tile.name} ${tile.valueLabel ?? "no value"}, ${style.label}`}
      className="flex flex-col gap-1.5 rounded-lg bg-gray-50 p-3 dark:bg-slate-900/40"
      data-testid="kpi-overview-tile"
      role="group"
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`h-2 w-2 shrink-0 rounded-full ${style.dot}`}
          data-testid="kpi-overview-tile-dot"
        />
        <span className="truncate text-xs text-gray-500 dark:text-gray-400">
          {tile.name}
        </span>
      </div>

      <div className="flex items-baseline gap-2 font-mono tabular-nums">
        <span className={`text-lg font-normal ${valueColor}`}>
          {tile.valueLabel ?? NO_VALUE}
        </span>
        {tile.changeLabel && (
          <span className={`text-xs ${CHANGE_TONE_STYLES[tile.changeTone]}`}>
            {tile.changeLabel}
          </span>
        )}
      </div>

      <div aria-hidden="true" className="relative h-8 w-full">
        <svg
          className="h-full w-full"
          preserveAspectRatio="none"
          viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
        >
          {tile.sparkline.area && (
            <polygon className={style.area} points={tile.sparkline.area} />
          )}
          {tile.sparkline.points && (
            <polyline
              className={style.stroke}
              fill="none"
              points={tile.sparkline.points}
              strokeLinejoin="round"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {last && (
          <span
            className={`absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${style.dot}`}
            data-testid="kpi-overview-tile-last-point"
            style={{
              left: `${(last.x / SPARKLINE_WIDTH) * 100}%`,
              top: `${(last.y / SPARKLINE_HEIGHT) * 100}%`,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default KPIOverviewTile;
